import { useEffect, useState } from 'react'

/**
 * Loads a report's data when it opens and again when `inputs` change. `failed` is set when the load
 * rejects, so the page can say so instead of showing an empty report.
 */
export function useLoaded<T>(load: () => Promise<T>, initial: T, inputs: unknown[] = []) {
  const [data, setData] = useState<T>(initial)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    // Marks a reload for changed inputs as loading before it is fetched; the
    // pattern the other report loaders use.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    load()
      .then((loaded) => {
        if (cancelled) return
        setData(loaded)
        setFailed(false)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // The loader is rebuilt on every render, so only `inputs` restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, inputs)

  return { data, loading, failed }
}
